import React, { useEffect, useState } from 'react';
import { inject, observer } from 'mobx-react';
import { Button, Flex, Group, Notification, Paper, Select, Stack, TextInput, Title, MultiSelect } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import * as z from 'zod';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { IconArrowLeft, IconBook, IconGenderBigender, IconStethoscope } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { AppMedError } from '../../../global/error/AppMedError.ts';
import { theme } from '../../../global/style/theme.tsx';
import { useCreateJournal, useUpdateJournal, useJournal } from '../../../global/entities/Journal/queries/useJournals.ts';
import { IJournalInput, IJournalCategory } from '../../../global/entities/Journal/Journal.ts';
import { JournalCategoryManager } from './JournalCategoryManager.tsx';
import { useGetTherapeuticIndications } from '../../../global/entities/TherapeuticIndication/queries/useGetTherapeuticIndications.tsx';
import { useGetSexes } from '../../../global/entities/Sexes/queries/useGetSexes.ts';

const initValues = {
    nameFr: '',
    nameEn: '',
    therapeuticIndicationID: '',
    sexIDs: [] as string[],
};

const JournalForm: React.FC = inject()(
    observer(() => {
        const { t } = useTranslation();
        const params = useParams<{ id: string }>();
        const [error, setError] = useState<string | null>(null);
        const [isSubmitting, setIsSubmitting] = useState(false);
        const [categories, setCategories] = useState<IJournalCategory[]>([]);
        const createJournalMutation = useCreateJournal();
        const updateJournalMutation = useUpdateJournal();
        const { data: journal } = useJournal(Number(params.id));
        const { data: therapeuticIndications } = useGetTherapeuticIndications();
        const { data: sexes } = useGetSexes();
        const navigate = useNavigate();

        // Validation schema
        const schema = z.object({
            nameFr: z.string().min(1, { message: t('formError.requiredName') + ' (FR)' }),
            nameEn: z.string().min(1, { message: t('formError.requiredName') + ' (EN)' }),
            therapeuticIndicationID: z.string().min(1, { message: t('formError.therapeuticIndicationIsRequired') }),
            sexIDs: z.array(z.string().min(1)).min(1, { message: t('formError.sexID') }),
        });

        const form = useForm<typeof initValues>({
            mode: 'controlled',
            initialValues: initValues,
            validate: zodResolver(schema),
        });

        const handleCancel = () => {
            form.reset();
            navigate('/adminDashboard/journal');
        };

        const handleSubmit = async (formValues: typeof initValues) => {
            setError(null);
            setIsSubmitting(true);
            try {
                const journalData: IJournalInput = {
                    nameFr: formValues.nameFr.trim(),
                    nameEn: formValues.nameEn.trim(),
                    therapeuticIndicationID: parseInt(formValues.therapeuticIndicationID),
                    sexIDs: formValues.sexIDs.map((v) => parseInt(v)),
                    categories: categories,
                };

                let savedJournalId: number;
                if (params.id !== '0') {
                    savedJournalId = Number(params.id);
                    await updateJournalMutation.mutateAsync({
                        id: savedJournalId,
                        journal: journalData,
                    });
                } else
                    savedJournalId = await createJournalMutation.mutateAsync(journalData);

                handleCancel();
            } catch (e) {
                if (e instanceof AppMedError)
                    setError(e.message);
                 else
                    setError('An unexpected error occurred.');
            } finally {
                setIsSubmitting(false);
            }
        };

        useEffect(() => {
            if (journal && params.id !== '0') {
                form.reset();
                form.setValues({
                    nameFr: journal.nameFr || '',
                    nameEn: journal.nameEn || '',
                    therapeuticIndicationID: journal.therapeuticIndicationID?.toString() || '',
                    sexIDs: journal.sexIDs?.map((id: number) => id.toString()) || [],
                });
                setCategories(journal.categories || []);
            }
        }, [journal, params.id]);

        // Transform API data to Select component format
        const therapeuticIndicationOptions = therapeuticIndications?.map(indication => ({
            value: indication.id.toString(),
            label: indication.name,
        })) || [];

        const sexOptions = sexes?.map(sex => ({
            value: sex.id.toString(),
            label: sex.name,
        })) || [];

        return (
            <>
                <Flex mb={'lg'} mt={'lg'} align={'center'} justify={'space-between'}>
                    <Button
                        leftSection={<IconArrowLeft color={theme.colors?.blue?.toString()} />}
                        onClick={() => navigate('/adminDashboard/journal')}
                        variant="transparent"
                    >
                        <Title c="black" order={4}>
                            {journal?.nameFr || t('journal.addNewJournal')}
                        </Title>
                    </Button>
                </Flex>
                <Paper p="md">
                    <form onSubmit={form.onSubmit(
                        (values) => handleSubmit(values),
                        (errors) => {
                            // Focus on the first field with an error
                            const firstErrorPath = Object.keys(errors)[0];
                            form.getInputNode(firstErrorPath)?.focus();
                        }
                    )}>
                        {error && (
                            <Notification color="red" withCloseButton={false} mb="md">
                                {error}
                            </Notification>
                        )}

                        <Stack gap="md">
                            <Group grow>
                                <TextInput
                                    label={t('formGlobal.name') + ' (Français)'}
                                    placeholder={t('journal.enterJournalName') + ' en français'}
                                    leftSection={<IconBook size={16} />}
                                    {...form.getInputProps('nameFr')}
                                />

                                <TextInput
                                    label={t('formGlobal.name') + ' (English)'}
                                    placeholder={t('journal.enterJournalName') + ' in English'}
                                    leftSection={<IconBook size={16} />}
                                    {...form.getInputProps('nameEn')}
                                />
                            </Group>

                            <Group grow>
                                <Select
                                    label={t('journal.therapeuticIndication')}
                                    placeholder={t('journal.selectTherapeuticIndication')}
                                    leftSection={<IconStethoscope size={16} />}
                                    data={therapeuticIndicationOptions}
                                    {...form.getInputProps('therapeuticIndicationID')}
                                />

                                <MultiSelect
                                    label={t('formGlobal.sex')}
                                    placeholder={t('journal.selectSex')}
                                    leftSection={<IconGenderBigender size={16} />}
                                    data={sexOptions}
                                    searchable
                                    clearable
                                    {...form.getInputProps('sexIDs')}
                                />
                            </Group>

                            {/* Categories and Questions Management */}
                            <JournalCategoryManager
                                categories={categories}
                                onCategoriesChange={setCategories}
                                journalId={params.id !== '0' ? Number(params.id) : undefined}
                            />
                        </Stack>

                        <Group justify="flex-end" mt="xl">
                            <Button variant="outline" onClick={handleCancel}>
                                {t('button.cancel')}
                            </Button>
                            <Button
                                type="submit"
                                loading={isSubmitting || createJournalMutation.isPending || updateJournalMutation.isPending}
                            >
                                {params.id !== '0' ? t('button.update') : t('button.create')}
                            </Button>
                        </Group>
                    </form>
                </Paper>
            </>
        );
    }),
);

export default JournalForm; 